import SaveIcon from '@mui/icons-material/Save';
import { LoadingButton } from '@mui/lab';
import { Box, Container, Grid, TextField, Typography } from '@mui/material';
import { useFormik } from 'formik';
import { useSnackbar } from 'notistack';
import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import * as Yup from 'yup';
import { useAppDispatch } from '../../../redux/hooks';
import {
  createCompanyWithdrawRequest,
  updateCompanyWithdrawRequest,
  fetchCompanyWithdrawRequest,
} from '../../../redux/modules/b2bThriftPlan/companies/company.actions';
import {
  companySelector,
  fetchCompanyWithdrawRequestLoadingSelector,
  updateCompanyWithdrawRequestLoadingSelector,
} from '../../../redux/modules/b2bThriftPlan/companies/company.selectors';
import { fetchStatuses } from '../../../constants/fetchStatuses';

const validationSchema = Yup.object({
  amount: Yup.number().required('Amount is required').positive('Amount must be positive'),
  reason: Yup.string().required('Reason is required'),
  beneficiaryName: Yup.string().required('Beneficiary Name is required'),
  beneficiaryIban: Yup.string().required('IBAN is required'),
  beneficiarySwiftCode: Yup.string().required('SWIFT Code is required'),
});

export function CreateWithdrawRequest() {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useAppDispatch();
  const { companyId, action } = useParams<{ companyId: string; action?: string }>();
  const isEditMode = !!action && action !== 'create';
  const withdrawRequestId = isEditMode ? action : undefined;
  const { createCompanyWithdrawRequestStatus, editCompanyWithdrawRequest } = useSelector(companySelector);
  const isFetchLoading = useSelector(fetchCompanyWithdrawRequestLoadingSelector);
  const isUpdateLoading = useSelector(updateCompanyWithdrawRequestLoadingSelector);
  const isCreateLoading = createCompanyWithdrawRequestStatus === fetchStatuses.pending;
  const isLoading = isCreateLoading || isUpdateLoading || isFetchLoading;

  // Fetch withdraw request data when in edit mode
  useEffect(() => {
    if (isEditMode && companyId && withdrawRequestId) {
      dispatch(fetchCompanyWithdrawRequest({ companyId: Number(companyId), id: Number(withdrawRequestId) }));
    }
  }, [isEditMode, companyId, withdrawRequestId, dispatch]);

  const { handleSubmit, values, handleChange, touched, errors, isValid, dirty, setValues } = useFormik({
    initialValues: {
      amount: '',
      reason: '',
      beneficiaryName: '',
      beneficiaryIban: '',
      beneficiarySwiftCode: '',
    },
    validationSchema,
    validateOnMount: true,
    enableReinitialize: true,
    onSubmit: async (formValues) => {
      if (!companyId) {
        enqueueSnackbar('Company ID is required', { variant: 'error' });
        return;
      }

      try {
        if (isEditMode && withdrawRequestId) {
          await dispatch(
            updateCompanyWithdrawRequest({
              companyId: Number(companyId),
              id: Number(withdrawRequestId),
              amount: Number(formValues.amount),
              reason: formValues.reason,
              beneficiaryName: formValues.beneficiaryName,
              beneficiaryIban: formValues.beneficiaryIban,
              beneficiarySwiftCode: formValues.beneficiarySwiftCode,
            }),
          ).unwrap();
          enqueueSnackbar('Withdraw request updated successfully!', { variant: 'success' });
        } else {
          await dispatch(
            createCompanyWithdrawRequest({
              companyId: Number(companyId),
              amount: Number(formValues.amount),
              reason: formValues.reason,
              beneficiaryName: formValues.beneficiaryName,
              beneficiaryIban: formValues.beneficiaryIban,
              beneficiarySwiftCode: formValues.beneficiarySwiftCode,
            }),
          ).unwrap();
          enqueueSnackbar('Withdraw request created successfully!', { variant: 'success' });
        }
        navigate(`/companies/${companyId}/withdraw-requests`);
      } catch (error: any) {
        const errorMessage = error?.message || `Failed to ${isEditMode ? 'update' : 'create'} withdraw request`;
        enqueueSnackbar(errorMessage, { variant: 'error' });
      }
    },
  });

  // Populate form when edit data is loaded
  useEffect(() => {
    if (isEditMode && editCompanyWithdrawRequest) {
      setValues({
        amount: String(editCompanyWithdrawRequest.amount || ''),
        reason: editCompanyWithdrawRequest.reason || '',
        beneficiaryName: editCompanyWithdrawRequest.beneficiaryName || '',
        beneficiaryIban: editCompanyWithdrawRequest.beneficiaryIban || '',
        beneficiarySwiftCode: editCompanyWithdrawRequest.beneficiarySwiftCode || '',
      });
    }
  }, [isEditMode, editCompanyWithdrawRequest, setValues]);

  return (
    <Container component="main" maxWidth="xl">
      <Box sx={{ pb: 5 }}>
        <Typography variant="h4">{isEditMode ? 'Edit Withdraw Request' : 'Create Withdraw Request'}</Typography>
      </Box>

      <form onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              margin="normal"
              id="amount"
              name="amount"
              label="Amount"
              type="number"
              value={values.amount}
              onChange={handleChange}
              error={touched.amount && !!errors.amount}
              helperText={touched.amount && errors.amount}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              margin="normal"
              id="reason"
              name="reason"
              label="Reason"
              value={values.reason}
              onChange={handleChange}
              error={touched.reason && !!errors.reason}
              helperText={touched.reason && errors.reason}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              margin="normal"
              id="beneficiaryName"
              name="beneficiaryName"
              label="Beneficiary Name"
              value={values.beneficiaryName}
              onChange={handleChange}
              error={touched.beneficiaryName && !!errors.beneficiaryName}
              helperText={touched.beneficiaryName && errors.beneficiaryName}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              margin="normal"
              id="beneficiaryIban"
              name="beneficiaryIban"
              label="IBAN"
              value={values.beneficiaryIban}
              onChange={handleChange}
              error={touched.beneficiaryIban && !!errors.beneficiaryIban}
              helperText={touched.beneficiaryIban && errors.beneficiaryIban}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              margin="normal"
              id="beneficiarySwiftCode"
              name="beneficiarySwiftCode"
              label="SWIFT Code"
              value={values.beneficiarySwiftCode}
              onChange={handleChange}
              error={touched.beneficiarySwiftCode && !!errors.beneficiarySwiftCode}
              helperText={touched.beneficiarySwiftCode && errors.beneficiarySwiftCode}
            />
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <LoadingButton
                type="submit"
                variant="contained"
                color="primary"
                loading={isLoading}
                startIcon={<SaveIcon />}
                disabled={!isValid || (!dirty && !isEditMode) || isLoading}
              >
                {isEditMode ? 'Update Withdraw Request' : 'Create Withdraw Request'}
              </LoadingButton>
            </Box>
          </Grid>
        </Grid>
      </form>
    </Container>
  );
}
